export interface ILinkingInfo {
  type: "C_V" | "V_J_V" | "V_W_V" | "C_C" | "ASSIMILATION";
  symbol: string;
  badge: string;
  color: string;
  label: string;
  description: string;
}

export const computeLinking = (w1: string, w2: string, ipa1: string, ipa2: string): ILinkingInfo | null => {
  if (!w1 || !w2) return null;
  const word1 = w1.toLowerCase().trim();
  const word2 = w2.toLowerCase().trim();

  // Clean IPA strings
  const cleanIpa1 = (ipa1 || "").replace(/[[\]/ˈˌ.: -]/g, "").trim();
  const cleanIpa2 = (ipa2 || "").replace(/[[\]/ˈˌ.: -]/g, "").trim();

  // 1. Assimilation (/t, d, s, z/ + /j/ in you, your, yet, etc.)
  const isYWord =
    /^(you|your|yours|yourself|yourselves|yet|year|yellow|young|use|used|usually|unit|unique|university|union)$/i.test(
      word2
    );
  if (isYWord) {
    if (/[t]$/.test(word1) || /te$/.test(word1) || cleanIpa1.endsWith("t")) {
      return {
        type: "ASSIMILATION",
        symbol: "‿",
        badge: "tʃ",
        color: "#EC4899",
        label: "Biến âm /tʃ/",
        description: `Biến âm: "${word1}" kết thúc bằng âm /t/ gặp "${word2}" (/j/) tạo thành âm /tʃ/ ("ch")`,
      };
    }
    if (/[d]$/.test(word1) || /de$/.test(word1) || cleanIpa1.endsWith("d")) {
      return {
        type: "ASSIMILATION",
        symbol: "‿",
        badge: "dʒ",
        color: "#EC4899",
        label: "Biến âm /dʒ/",
        description: `Biến âm: "${word1}" kết thúc bằng âm /d/ gặp "${word2}" (/j/) tạo thành âm /dʒ/ ("j/đz")`,
      };
    }
    if (/[s]$/.test(word1) || /se$/.test(word1) || /ss$/.test(word1) || cleanIpa1.endsWith("s")) {
      return {
        type: "ASSIMILATION",
        symbol: "‿",
        badge: "ʃ",
        color: "#EC4899",
        label: "Biến âm /ʃ/",
        description: `Biến âm: "${word1}" kết thúc bằng âm /s/ gặp "${word2}" (/j/) tạo thành âm /ʃ/ ("sh")`,
      };
    }
    if (/[z]$/.test(word1) || /ze$/.test(word1) || cleanIpa1.endsWith("z")) {
      return {
        type: "ASSIMILATION",
        symbol: "‿",
        badge: "ʒ",
        color: "#EC4899",
        label: "Biến âm /ʒ/",
        description: `Biến âm: "${word1}" kết thúc bằng âm /z/ gặp "${word2}" (/j/) tạo thành âm /ʒ/ ("zh")`,
      };
    }
  }

  // Check if word2 starts with a vowel sound
  const isWInitialWord = /^(one|once|one's|oneself)$/i.test(word2);
  const isYInitialWord =
    /^(university|uniform|union|unit|unique|universal|useful|user|use|used|usually|european)$/i.test(word2);
  const startsWithVowelLetter = /^[aeiou]/i.test(word2) && !isWInitialWord && !isYInitialWord;
  const startsWithVowelIpa = /^[aeiouɑɒɔʌəɜɛɪiʊuæ]/.test(cleanIpa2);
  const isWord2VowelInitial = startsWithVowelIpa || startsWithVowelLetter;

  // 2. Vowel to Vowel (V + /j/ + V)
  const isVowelJEnding =
    /(iː|i|ɪ|eɪ|aɪ|ɔɪ)$/.test(cleanIpa1) ||
    /^(i|my|we|he|she|be|me|see|tree|free|the|they|say|day|may|pay|play|stay|way|away|today|buy|fly|try|why|die|lie|tie|pie|eye|boy|toy|joy|key|tea|sea|coffee)$/i.test(
      word1
    ) ||
    /(y|ee|ea|ey|ay|oy|ie|igh)$/i.test(word1);

  if (isVowelJEnding && isWord2VowelInitial) {
    return {
      type: "V_J_V",
      symbol: "‿",
      badge: "+j",
      color: "#F59E0B",
      label: "Chèn /j/",
      description: `Nối nguyên âm: "${word1}" kết thúc bằng âm đuôi dẹt/kéo dài, chèn âm lướt /j/ (y) sang "${word2}"`,
    };
  }

  // 3. Vowel to Vowel (V + /w/ + V)
  const isVowelWEnding =
    /(uː|u|ʊ|oʊ|əʊ|ou|au|aʊ|ɔː|o)$/.test(cleanIpa1) ||
    /^(to|do|you|who|two|go|so|no|too|how|now|cow|show|know|grow|throw|blow|flow|new|few|through|blue|true|shoe|zoo|also|into|hello)$/i.test(
      word1
    ) ||
    /(oo|ow|ew|ue|ou)$/i.test(word1) ||
    (/[o]$/i.test(word1) && !/^(to|do|into)$/i.test(word1));

  if (isVowelWEnding && isWord2VowelInitial) {
    return {
      type: "V_W_V",
      symbol: "‿",
      badge: "+w",
      color: "#F97316",
      label: "Chèn /w/",
      description: `Nối nguyên âm: "${word1}" kết thúc bằng âm đuôi tròn môi, chèn âm lướt /w/ sang "${word2}"`,
    };
  }

  // 4. Consonant to Consonant (Same sound C + C)
  const lastChar1 = word1.replace(/e$/, "").slice(-1);
  const firstChar2 = word2.slice(0, 1);
  const isSameConsonant =
    lastChar1 &&
    firstChar2 &&
    lastChar1 === firstChar2 &&
    /[bcdfghjklmnpqrstvwxyz]/.test(lastChar1) &&
    !isWord2VowelInitial;

  if (isSameConsonant) {
    return {
      type: "C_C",
      symbol: "‿",
      badge: "C-C",
      color: "#8B5CF6",
      label: "Nối C-C",
      description: `Nối phụ âm cùng loại: "${word1}" và "${word2}" chung âm /${lastChar1}/, giữ khẩu hình không bật âm 2 lần`,
    };
  }

  // 5. Consonant to Vowel (C + V)
  const isVowelEnding = isVowelJEnding || isVowelWEnding || /(ə|ɑː|ɜː|æ|ʌ|ɒ|e|a)$/.test(cleanIpa1);
  if (isWord2VowelInitial && !isVowelEnding) {
    return {
      type: "C_V",
      symbol: "‿",
      badge: "C-V",
      color: "#10B981",
      label: "Nối C-V",
      description: `Nối phụ âm sang nguyên âm: Đọc phụ âm cuối của "${word1}" nối liền vào nguyên âm đầu của "${word2}"`,
    };
  }

  return null;
};
